import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/contexts';
import { EmptyObject, ErrorI, OnDemandHoursI, PostOnDemandHoursI } from '@/types/cyclone/requests';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { Button } from '@/components';
import { ScheduleI } from '@/containers/ActionTeam/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { scrollBarTW } from '@/components/App';
import { useClient, useWindowResize } from '@/hooks';
import { InputDatepicker, InputTimePicker } from '@/components/Input';
import { VendorOnDemandHoursI } from '@/types/cyclone/models';
import { useMutation } from 'react-query';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { Clock, Plus } from 'lucide-react';

type Props = {
  show: boolean;
  setShow: (show: boolean) => void;
  refetch: () => void;
  onDemandHour?: VendorOnDemandHoursI;
  serviceId: number;
};

const TODAY = new Date();
const DEFAULT_SCHEDULE = { start_time: '09:00', end_time: '12:00' };

export const AvailabilityModal: React.FC<Props> = ({ show, setShow, onDemandHour, refetch, serviceId }) => {
  const { session } = useAuth();
  const { client } = useClient();
  const { isMobileSize } = useWindowResize();

  const [schedulesSelected, setSchedulesSelected] = useState<ScheduleI[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(TODAY);
  const [isLoading, setIsLoading] = useState(false);

  const isEdit = Boolean(onDemandHour);

  useEffect(() => {
    if (show) {
      if (onDemandHour) {
        const schedules = onDemandHour.working_hours.map((schedule) => ({
          start_time: schedule.start_time,
          end_time: schedule.end_time
        }));
        setSchedulesSelected(schedules);
        setSelectedDate(dayjs(onDemandHour.start_at).toDate());
      } else {
        // Reset for new entry
        setSchedulesSelected([DEFAULT_SCHEDULE]);
        setSelectedDate(TODAY);
      }
    }
  }, [show, onDemandHour]);

  const { isSaveDisabled, isAddScheduleDisabled } = useMemo(() => {
    const hasEmptyFields = schedulesSelected.some((schedule) => !schedule.start_time || !schedule.end_time);

    return {
      isSaveDisabled: hasEmptyFields || schedulesSelected.length === 0,
      isAddScheduleDisabled: hasEmptyFields
    };
  }, [schedulesSelected]);

  const handleSelectChange = (value: string, index: number, key: keyof ScheduleI) => {
    setSchedulesSelected((prev) => {
      const updated = prev.map((schedule, i) => (i === index ? { ...schedule, [key]: value } : schedule));

      if (key === 'end_time' && index < prev.length - 1) {
        updated[index + 1] = { ...updated[index + 1], start_time: value };
      }

      return updated;
    });
  };

  const deleteSchedule = (index: number) => {
    setSchedulesSelected((prev) => {
      const updated = prev.filter((_, i) => i !== index);

      if (index < prev.length - 1 && index > 0) {
        updated[index] = {
          ...updated[index],
          start_time: prev[index - 1].end_time
        };
      }

      return updated;
    });
  };

  const addNewSchedule = () => {
    setSchedulesSelected((prev) => {
      const lastSchedule = prev[prev.length - 1];
      return [
        ...prev,
        {
          start_time: lastSchedule.end_time,
          end_time: ''
        }
      ];
    });
  };

  const validateWorkingHours = (workingHours: OnDemandHoursI['working_hours']): boolean => {
    const isTimeBefore = (time1: string, time2: string): boolean => {
      const [hours1, minutes1] = time1.split(':').map(Number);
      const [hours2, minutes2] = time2.split(':').map(Number);
      return hours1 < hours2 || (hours1 === hours2 && minutes1 < minutes2);
    };

    const sortedHours = [...workingHours].sort((a, b) => (isTimeBefore(a.start_time, b.start_time) ? -1 : 1));

    for (let i = 0; i < sortedHours.length; i++) {
      const current = sortedHours[i];

      if (isTimeBefore(current.end_time, current.start_time) || current.end_time === current.start_time) {
        toast.error(
          `El horario de fin debe ser después del horario de inicio en el rango ${current.start_time} - ${current.end_time}.`
        );
        return false;
      }
    }

    return true;
  };

  const mutation = useMutation<EmptyObject, ErrorI, PostOnDemandHoursI>(
    (data) =>
      client(`me/vendor/on_demand_hours`, 'POST', {
        isAuthRequired: true,
        data
      }),
    {
      onMutate: () => {
        setIsLoading(true);
      },
      onSuccess: () => {
        toast.success('Horarios guardados exitosamente.');
        refetch();
        setShow(false);
      },
      onError: (e) => {
        if (e.code && e.code === 'ODH-001') {
          toast.error('Los horarios ingresados colisionan con otros horarios laborales recurrentes.');
        } else {
          toast.error('Algo anda mal. Por favor, contactar a soporte.');
        }
      },
      onSettled: () => {
        setIsLoading(false);
      }
    }
  );

  const mutationEdit = useMutation<EmptyObject, ErrorI, PostOnDemandHoursI>(
    (data) =>
      client(`me/vendor/on_demand_hours/${onDemandHour?.id}`, 'PUT', {
        isAuthRequired: true,
        data
      }),
    {
      onMutate: () => {
        setIsLoading(true);
      },
      onSuccess: () => {
        toast.success('Horarios guardados exitosamente.');
        refetch();
        setShow(false);
      },
      onError: (e) => {
        if (e.code && e.code === 'ODH-001') {
          toast.error('Los horarios ingresados colisionan con otros horarios laborales recurrentes.');
        } else {
          toast.error('Algo anda mal. Por favor, contactar a soporte.');
        }
      },
      onSettled: () => {
        setIsLoading(false);
      }
    }
  );

  const handleSave = () => {
    const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');

    const onDemandHours: OnDemandHoursI = {
      service_ids: [serviceId],
      start_at: isEdit ? (onDemandHour?.start_at as string) : formattedDate,
      working_hours: schedulesSelected.map((schedule) => ({
        start_time: schedule.start_time,
        end_time: schedule.end_time
      }))
    };

    // Validate before submission
    if (!validateWorkingHours(onDemandHours.working_hours)) return;

    const data: PostOnDemandHoursI = {
      on_demand_hours: onDemandHours,
      vendor_id: session?.vendor?.id.toString(),
      is_unique_for_service: true
    };

    if (isEdit) {
      mutationEdit.mutate(data);
    } else {
      mutation.mutate(data);
    }
  };

  const subtitle = isEdit
    ? `Edita los horarios disponibles para el ${dayjs(onDemandHour?.start_at).format('dddd, DD MMMM YYYY')}`
    : 'Selecciona una fecha y configura los horarios disponibles para este servicio.';

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        setShow(open);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-3 border-b border-[#DACCE0]">
          <div className="flex justify-between items-center w-full">
            <DialogClose />
            <DialogTitle className="text-center flex-1">
              {isEdit ? 'Editar horarios' : 'Agregar horarios'}
            </DialogTitle>
            <div className="w-8" />
          </div>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm font-book text-gray-600 mb-4">{subtitle}</p>

          {!isEdit && (
            <div className="mb-6">
              <InputDatepicker
                date={selectedDate}
                setDate={setSelectedDate}
                datepickerProps={{ disabled: isLoading }}
              />
            </div>
          )}

          <div className="flex items-center gap-2 mb-3 font-medium">
            <Clock size="16" />
            <span>Rangos horarios</span>
          </div>

          <div className={`max-h-[240px] md:max-h-[185px] overflow-y-auto ${scrollBarTW} mb-3`}>
            {schedulesSelected.map((schedule, i) => {
              const previousSchedule = schedulesSelected[i - 1];
              const nextSchedule = schedulesSelected[i + 1];

              const minTime = previousSchedule ? previousSchedule.end_time : undefined;
              const maxTime = nextSchedule ? nextSchedule.start_time : undefined;
              return (
                <div key={i} className="flex items-center justify-between gap-2 mb-3 group">
                  <div className="flex items-center gap-2 w-[calc(100%-40px)]">
                    <div className="w-1/2">
                      <InputTimePicker
                        initialValue={schedule.start_time}
                        onTimeChange={(time) => handleSelectChange(time, i, 'start_time')}
                        minTime={minTime}
                        maxTime={schedule.end_time}
                      />
                    </div>
                    <div className="w-1/2">
                      <InputTimePicker
                        initialValue={schedule.end_time}
                        onTimeChange={(time) => handleSelectChange(time, i, 'end_time')}
                        minTime={schedule.start_time}
                        maxTime={maxTime}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => deleteSchedule(i)}
                    disabled={schedulesSelected.length === 1 || isLoading}
                    className={classNames(
                      'h-8 w-8 flex items-center justify-center rounded-full transition-all',
                      schedulesSelected.length === 1
                        ? 'opacity-30 cursor-not-allowed'
                        : 'hover:bg-red-50 opacity-60 group-hover:opacity-100'
                    )}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} className="text-red-500" size="sm" />
                  </button>
                </div>
              );
            })}
          </div>

          <button
            onClick={addNewSchedule}
            disabled={isAddScheduleDisabled || isLoading}
            className={classNames(
              'w-full py-2 border border-dashed rounded-md flex items-center justify-center gap-1 transition-all',
              isAddScheduleDisabled || isLoading
                ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                : 'border-blue/20 text-blue hover:bg-blue/10 cursor-pointer'
            )}
          >
            <Plus size={16} />
            <span className="font-medium">Agregar nuevo rango</span>
          </button>
        </div>

        <DialogFooter fixed={isMobileSize}>
          <Button
            rounded
            fullWidth
            onClick={handleSave}
            disabled={isSaveDisabled || isLoading}
            loading={isLoading}
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
